/**
 * Quick Scan — run a fresh Local Falcon scan for the selected client.
 *
 * This is the ONLY control in the product that spends Local Falcon credits, so
 * the dialog is built around making that cost impossible to miss: the estimate
 * is shown next to the confirm button and updates with the grid size, which is
 * the setting that actually drives the bill (cost grows with the SQUARE of the
 * grid — a 7x7 costs nearly twice a 5x5).
 */
import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, Loader2, MapPin, Search } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	AI_ANALYSIS_CREDITS,
	estimateScanCredits,
	gbpRecommendationsService,
	type QuickScanParams,
} from "@/services/gbpRecommendationsService";
import { AISuggest } from "@/components/brand-bible/AISuggest";
import ScanGridMap, {
	type LatLng,
} from "@/components/GoogleBusinessProfile/ScanGridMap";

// Every size Local Falcon's run-scan enum accepts (openapi.yaml, 2026-09-02).
// The big ones are here because a 21x21 is what a multi-suburb service area
// actually needs — and they are the reason the cost line below shouts.
const GRID_SIZES = [3, 5, 7, 9, 11, 13, 15, 17, 19, 21] as const;

// Above this the bill stops being incidental (13x13 = 194 credits with the AI
// analysis), so the cost line changes from a note into a warning.
const LARGE_GRID = 13;

export interface QuickScanDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	tenantId: string;
	locationName: string;
	/** False when the mapping has no coordinates — a scan cannot be started. */
	canScan: boolean;
	/**
	 * The mapped listing's coordinates, as the API returns them (strings). The
	 * grid starts here, and "Re-centre on the business" comes back to it.
	 */
	businessLat?: string;
	businessLng?: string;
	onConfirm: (params: QuickScanParams) => void;
	isSubmitting?: boolean;
	error?: string | null;
}

export default function QuickScanDialog({
	open,
	onOpenChange,
	tenantId,
	locationName,
	canScan,
	businessLat,
	businessLng,
	onConfirm,
	isSubmitting = false,
	error = null,
}: QuickScanDialogProps) {
	const [keyword, setKeyword] = useState("");
	const [gridSize, setGridSize] = useState<number>(5);
	const [radius, setRadius] = useState<number>(1);
	const [measurement, setMeasurement] = useState<"mi" | "km">("mi");
	// Default ON: it is what the recommendations page renders, and the vendor
	// gives us no way to add an analysis to a report afterwards.
	const [aiAnalysis, setAiAnalysis] = useState(true);
	// null means "wherever the business is" — the backend then uses the mapped
	// coordinates, so an unplotted scan behaves exactly as it did before.
	const [center, setCenter] = useState<LatLng | null>(null);

	const business = useMemo<LatLng | null>(() => {
		const lat = Number(businessLat);
		const lng = Number(businessLng);
		if (!businessLat || !businessLng || Number.isNaN(lat) || Number.isNaN(lng)) {
			return null;
		}
		return { lat, lng };
	}, [businessLat, businessLng]);

	// Reset on open so a previous keyword — or a grid plotted over a different
	// client's town — can never be re-submitted by accident.
	useEffect(() => {
		if (open) {
			setKeyword("");
			setGridSize(5);
			setRadius(1);
			setMeasurement("mi");
			setCenter(null);
			setAiAnalysis(true);
		}
	}, [open]);

	const trimmed = keyword.trim();
	const credits = estimateScanCredits(gridSize, aiAnalysis);
	const unitLabel = measurement === "km" ? "kilometres" : "miles";
	const canSubmit = Boolean(trimmed) && canScan && !isSubmitting;
	const plotted = center !== null;

	const submit = () => {
		if (!canSubmit) return;
		onConfirm({
			tenantId,
			keyword: trimmed,
			gridSize,
			radius,
			measurement,
			aiAnalysis,
			...(center ? { centerLat: center.lat, centerLng: center.lng } : {}),
		});
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-lg" data-testid="quick-scan-dialog">
				<DialogHeader>
					<DialogTitle className="flex items-center gap-2">
						<Search className="h-4 w-4 text-coral" />
						Quick Scan
					</DialogTitle>
					<DialogDescription>
						Run a new profile scan for{" "}
						<span className="font-medium text-foreground">
							{locationName || tenantId}
						</span>
						, then read the recommendations from the fresh report.
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-4">
					<div className="space-y-1.5">
						<div className="flex items-center justify-between gap-2">
							<Label htmlFor="quick-scan-keyword">Keyword</Label>
							{/* Ideas, not a decision: clicking one fills the field and
							    nothing else. The operator still presses the confirm
							    button, so a suggestion can never spend a credit on its
							    own. Suggesting itself is free. */}
							<div data-testid="quick-scan-keyword-suggest">
								<AISuggest
									sectionId={`gbp-quick-scan-keyword:${tenantId}`}
									kind="chips"
									placeholder="Suggest keywords"
									source="from this client's services"
									/* KEEP ON main. AISuggest here still carries the
									   Brand Bible release gate, and Quick Scan is not
									   part of that rollout -- without this the chips
									   render nothing in production while dev, which has
									   no gate, works fine. main fixed that; staging
									   removed the gate from the component instead, so
									   its copy of this file does not need the prop.
									   Promoting staging's file verbatim would re-break
									   the production bug (2026-09-23). */
									releaseGated={false}
									existing={trimmed ? [trimmed] : []}
									onAccept={(item) => setKeyword(item)}
									fetcher={async () => ({
										items: await gbpRecommendationsService.suggestKeywords(
											tenantId,
										),
										source: "from this client's services",
									})}
								/>
							</div>
						</div>
						<Input
							id="quick-scan-keyword"
							data-testid="quick-scan-keyword"
							placeholder="e.g. emergency plumber"
							value={keyword}
							onChange={(e) => setKeyword(e.target.value)}
							onKeyDown={(e) => {
								if (e.key === "Enter") submit();
							}}
							disabled={isSubmitting}
						/>
						<p className="text-xs text-muted-foreground">
							The search term you want this business to rank for locally.
							Leave the place name out — the grid already moves across the
							map.
						</p>
					</div>

					<div className="grid grid-cols-3 gap-3">
						<div className="space-y-1.5">
							<Label htmlFor="quick-scan-grid">Grid</Label>
							<select
								id="quick-scan-grid"
								data-testid="quick-scan-grid"
								className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
								value={gridSize}
								onChange={(e) => setGridSize(Number(e.target.value))}
								disabled={isSubmitting}
							>
								{GRID_SIZES.map((size) => (
									<option key={size} value={size}>
										{size} × {size}
									</option>
								))}
							</select>
						</div>
						<div className="space-y-1.5">
							{/* The unit lives in the label: a bare number beside a
							    separate Units select is a number in no unit at all. */}
							<Label htmlFor="quick-scan-radius">
								Radius ({measurement})
							</Label>
							<Input
								id="quick-scan-radius"
								data-testid="quick-scan-radius"
								type="number"
								min={0.1}
								step={0.1}
								value={radius}
								onChange={(e) => setRadius(Number(e.target.value))}
								disabled={isSubmitting}
							/>
						</div>
						<div className="space-y-1.5">
							<Label htmlFor="quick-scan-measurement">Units</Label>
							<select
								id="quick-scan-measurement"
								data-testid="quick-scan-measurement"
								className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
								value={measurement}
								onChange={(e) =>
									setMeasurement(e.target.value === "km" ? "km" : "mi")
								}
								disabled={isSubmitting}
							>
								<option value="mi">Imperial (mi)</option>
								<option value="km">Metric (km)</option>
							</select>
						</div>
					</div>

					{/* The one setting here that changes WHAT you get back, not
					    just where you look. Off is cheaper and permanent. */}
					<label
						className="flex cursor-pointer items-start gap-2 rounded-lg border border-border p-3"
						htmlFor="quick-scan-ai"
					>
						<input
							id="quick-scan-ai"
							data-testid="quick-scan-ai"
							type="checkbox"
							className="mt-0.5 h-4 w-4 accent-coral"
							checked={aiAnalysis}
							onChange={(e) => setAiAnalysis(e.target.checked)}
							disabled={isSubmitting}
						/>
						<span className="text-sm">
							<span className="font-medium text-foreground">
								Generate the AI analysis
							</span>{" "}
							<span className="text-muted-foreground">
								(+{AI_ANALYSIS_CREDITS} credits)
							</span>
							<span className="mt-1 block text-xs text-muted-foreground">
								{aiAnalysis
									? "The written recommendations, citations and beatable competitors this page is built on."
									: "Ranking data only. This page will have no recommendations for the scan, and the analysis cannot be added to it afterwards — that needs a fresh, fully-charged scan."}
							</span>
						</span>
					</label>

					{/* Where the grid lands. Drawn from the same radius/grid numbers
					    the vendor is sent, so what is on screen is what is bought. */}
					{business && (
						<div className="space-y-1.5">
							<div className="flex items-center justify-between">
								<Label>Grid centre</Label>
								{plotted && (
									<button
										type="button"
										className="text-xs text-coral underline-offset-2 hover:underline"
										onClick={() => setCenter(null)}
										disabled={isSubmitting}
										data-testid="quick-scan-recenter"
									>
										Re-centre on the business
									</button>
								)}
							</div>
							<ScanGridMap
								center={center ?? business}
								business={business}
								gridSize={gridSize}
								radius={radius}
								measurement={measurement}
								onCenterChange={setCenter}
								disabled={isSubmitting}
							/>
							<p
								className="flex items-center gap-1 text-xs text-muted-foreground"
								data-testid="quick-scan-center"
							>
								<MapPin className="h-3 w-3 shrink-0" />
								{plotted ? (
									<>
										Grid centred on {center.lat.toFixed(5)},{" "}
										{center.lng.toFixed(5)}, reaching {radius} {unitLabel} out
										— click or drag the pin to move it.
									</>
								) : (
									<>
										Centred on the business, reaching {radius} {unitLabel} out.
										Click the map, or drag the pin, to scan a different part of
										the area.
									</>
								)}
							</p>
						</div>
					)}

					{/* The cost line is the point of this dialog. */}
					<div
						className="flex items-start gap-2 rounded-lg border border-coral/40 bg-coral-light p-3"
						data-testid="quick-scan-cost"
					>
						<AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-coral-dark" />
						<p className="text-sm text-coral-dark">
							This spends about{" "}
							<span className="font-semibold">{credits} scan credits</span>{" "}
							({gridSize * gridSize} grid points
							{aiAnalysis
								? ` + ${AI_ANALYSIS_CREDITS} for the AI analysis`
								: ", no AI analysis"}
							). Credits are shared across every client on the agency
							account.
							{gridSize >= LARGE_GRID && (
								<>
									{" "}
									<span className="font-semibold">
										A {gridSize} × {gridSize} grid is a large scan
									</span>{" "}
									— roughly{" "}
									{Math.round(credits / estimateScanCredits(5, aiAnalysis))}x a
									5 × 5. Check the balance before confirming.
								</>
							)}
						</p>
					</div>

					{!canScan && (
						<p
							className="text-sm text-destructive"
							data-testid="quick-scan-cannot"
						>
							This client has no Google Business Profile location with
							coordinates, so a scan cannot be started. Map the location first.
						</p>
					)}

					{error && (
						<p className="text-sm text-destructive" data-testid="quick-scan-error">
							{error}
						</p>
					)}
				</div>

				<DialogFooter>
					<Button
						variant="outline"
						onClick={() => onOpenChange(false)}
						disabled={isSubmitting}
					>
						Cancel
					</Button>
					<Button
						onClick={submit}
						disabled={!canSubmit}
						data-testid="quick-scan-confirm"
					>
						{isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
						Run scan ({credits} credits)
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
